import React, { useState } from 'react';
import { X, PenLine, Plus, Star, Trash2 } from 'lucide-react';
import QuillEditor from '../../MasterAdmin/ui/QuillEditor';
import type { EmailSignature, SignatureImage } from '../../../models/MasterAdmin/marketingEmail';
import { buildSignatureBlock, signatureImageHtml } from './marketingEmailHtml';
import {
  MARKETING_EMAIL_FONT_FAMILY,
  MARKETING_EMAIL_FONT_SIZE,
} from './marketingEmailTypography';
import { prepareMarketingEmailHtmlForPreview } from './marketingEmailHtmlFormatter';
import EmailHtmlPreview from './EmailHtmlPreview';
import {
  meGhostBtnClass,
  meInputClass,
  meLabelClass,
  meModalOverlayClass,
  meModalPanelClass,
  mePrimaryBtnClass,
  meSecondaryBtnClass,
} from './marketingEmailUi';

interface SignatureManagerModalProps {
  open: boolean;
  signatures: EmailSignature[];
  signatureImages?: SignatureImage[];
  onClose: () => void;
  onSave: (signatures: EmailSignature[]) => void;
}

function newSignatureId() {
  return `sig-${Date.now()}`;
}

const SignatureManagerModal: React.FC<SignatureManagerModalProps> = ({
  open,
  signatures,
  signatureImages = [],
  onClose,
  onSave,
}) => {
  const [items, setItems] = useState<EmailSignature[]>(signatures);
  const [editing, setEditing] = useState<EmailSignature | null>(null);
  const [form, setForm] = useState({ name: '', htmlBody: '' });
  const [editorSessionKey, setEditorSessionKey] = useState(0);

  React.useEffect(() => {
    if (open) {
      setItems(signatures);
      setEditing(null);
      setForm({ name: '', htmlBody: '' });
    }
  }, [open, signatures]);

  if (!open) return null;

  const startCreate = () => {
    setEditing({ id: newSignatureId(), name: '', htmlBody: '', isDefault: false, createdAt: '', updatedAt: '' });
    setEditorSessionKey((k) => k + 1);
    setForm({ name: '', htmlBody: `<p>Regards,</p><p><strong>Kapil Kalyani</strong> (Founder &amp; COO)<br/>Vedika Healthtech Pvt. Ltd.</p><p><a href="mailto:tech@vedika.health">tech@vedika.health</a> | <a href="https://www.vedika.health">www.vedika.health</a> | LinkedIn | +91 8855815433</p><p style="font-size:12px;color:#6b7280;">Office: 198 City Hub, 3rd Floor, Office No.311, Sankar Nanasaheb Karpe Road, Ganesh Peth, Pune – 411002. MH.</p>` });
  };

  const startEdit = (sig: EmailSignature) => {
    setEditing(sig);
    setEditorSessionKey((k) => k + 1);
    setForm({ name: sig.name, htmlBody: sig.htmlBody || '' });
  };

  const commitFormToItems = (currentItems: EmailSignature[]): EmailSignature[] => {
    if (!editing || !form.name.trim()) return currentItems;
    const now = new Date().toISOString();
    const entry: EmailSignature = {
      ...editing,
      name: form.name.trim(),
      htmlBody: form.htmlBody,
      createdAt: editing.createdAt || now,
      updatedAt: now,
    };
    const exists = currentItems.some((s) => s.id === entry.id);
    return exists ? currentItems.map((s) => (s.id === entry.id ? entry : s)) : [...currentItems, entry];
  };

  const saveForm = () => {
    if (!editing || !form.name.trim()) return;
    setItems(commitFormToItems(items));
    setEditing(null);
    setForm({ name: '', htmlBody: '' });
  };

  const setDefault = (id: string) => {
    setItems(items.map((s) => ({ ...s, isDefault: s.id === id })));
  };

  const remove = (id: string) => {
    if (items.length <= 1) return;
    const next = items.filter((s) => s.id !== id);
    if (!next.some((s) => s.isDefault)) next[0].isDefault = true;
    setItems(next);
    if (editing?.id === id) setEditing(null);
  };

  const handleSaveAll = () => {
    const nextItems = editing ? commitFormToItems(items) : items;
    onSave(nextItems);
    onClose();
  };

  return (
    <div className={meModalOverlayClass}>
      <div className={`${meModalPanelClass} max-w-3xl`}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Email signatures</h2>
          <button type="button" onClick={onClose} className={meGhostBtnClass}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 gap-0 overflow-hidden md:grid-cols-2">
          <div className="overflow-y-auto border-b border-gray-200 p-4 dark:border-gray-800 md:border-b-0 md:border-r">
            <button type="button" onClick={startCreate} className={`${meSecondaryBtnClass} mb-4 w-full`}>
              <Plus className="h-4 w-4" />
              New signature
            </button>
            <div className="space-y-2">
              {items.map((sig) => (
                <div
                  key={sig.id}
                  className={`rounded-xl border p-3 transition ${
                    editing?.id === sig.id
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" onClick={() => startEdit(sig)} className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-white">{sig.name}</span>
                        {sig.isDefault && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            Default
                          </span>
                        )}
                      </div>
                    </button>
                    <div className="flex shrink-0 gap-1">
                      {!sig.isDefault && (
                        <button type="button" onClick={() => setDefault(sig.id)} title="Set as default" className={meGhostBtnClass}>
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button type="button" onClick={() => startEdit(sig)} className={meGhostBtnClass}>
                        <PenLine className="h-4 w-4" />
                      </button>
                      {items.length > 1 && (
                        <button type="button" onClick={() => remove(sig.id)} className={`${meGhostBtnClass} text-red-500`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-y-auto p-4">
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className={meLabelClass}>Signature name</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Marketing Team"
                    className={meInputClass}
                  />
                </div>
                <div>
                  <label className={meLabelClass}>Signature content</label>
                  {signatureImages.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {signatureImages.map((img) => (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              htmlBody: `${f.htmlBody}${signatureImageHtml(img)}`,
                            }))
                          }
                          className="rounded-lg border border-gray-200 p-1 hover:border-primary dark:border-gray-700"
                          title={`Insert ${img.name}`}
                        >
                          <img src={img.url} alt={img.name} className="h-8 w-auto object-contain" />
                        </button>
                      ))}
                      <span className="self-center text-xs text-gray-400">Click to insert image</span>
                    </div>
                  )}
                  <QuillEditor
                    key={`${editing?.id ?? 'new-signature'}-${editorSessionKey}`}
                    value={form.htmlBody}
                    onChange={(htmlBody) => setForm((f) => ({ ...f, htmlBody }))}
                    height="180px"
                    placeholder="Your signature…"
                    defaultFontFamily={MARKETING_EMAIL_FONT_FAMILY}
                    defaultFontSize={MARKETING_EMAIL_FONT_SIZE}
                    useSemanticHtml
                    allowBold
                  />
                </div>
                <EmailHtmlPreview
                  html={prepareMarketingEmailHtmlForPreview(
                    buildSignatureBlock(
                      { id: editing.id, name: form.name, htmlBody: form.htmlBody, isDefault: editing.isDefault, createdAt: editing.createdAt, updatedAt: editing.updatedAt },
                      signatureImages
                    )
                  )}
                  compact
                  minHeight="140px"
                />
                <div className="flex gap-2">
                  <button type="button" onClick={saveForm} className={mePrimaryBtnClass}>
                    Save signature
                  </button>
                  <button type="button" onClick={() => setEditing(null)} className={meSecondaryBtnClass}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select a signature to edit, or create a new one. The default signature is auto-applied in compose when
                enabled.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-800">
          <button type="button" onClick={onClose} className={meSecondaryBtnClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSaveAll} className={mePrimaryBtnClass}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureManagerModal;
